import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { DefaultsPageContext } from 'features/globalSetup/contexts/DefaultsPageContext'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { Schema } from '../../schema'
import { Data, toDTO, UPDATE_WORKSPACE_PREBID_CONFIGURATION } from './api'
import { Variables } from './api/types'
import { UseUpdateWorkspacePrebidConfiguration } from './types'

export const useUpdateWorkspacePrebidConfiguration = (): UseUpdateWorkspacePrebidConfiguration => {
  const { t } = useTranslation('features/globalSetup')
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})
  const { setLoading } = useContext(DefaultsPageContext)

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_WORKSPACE_PREBID_CONFIGURATION,
    onCompleted: async ({
      updateWorkspacePrebidConfiguration: { errors },
    }: Data): Promise<void> => {
      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('defaults.prebid.action.error'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('defaults.prebid.action.success'), {
          variant: 'success',
        })
      }
    },
    queriesToInvalidate: ['getWorkspacePrebidConfiguration'],
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const update = (data: Schema): void => {
    mutateAsync({ input: toDTO(data) })
  }

  return {
    errors,
    update,
  }
}
